import React from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {Button, EMPHASIS, FlexColumn, FlexRow, Heading, Table, Text} from '@forecast-it/design-system';
import {
	workDetailHours,
	workDetailTotalMinutes,
	workDetailTotalValue,
	WorkDetailRow,
} from '../../../../components/insights/invoicing/line-generation/invoicing_work_detail_rows';
import {workDetailUngroupedLabelKey} from '../../../../components/insights/invoicing/line-generation/invoicing_groupings';

/**
 * A layout-neutral carrier for a test or Cypress hook.
 *
 * The design-system text and layout components destructure a fixed prop list and forward only
 * `data-cy` — `data-testid` never reaches the DOM through them. Rather than reach past the design
 * system for typography just to keep a hook, the hook goes on this wrapper and the design-system
 * component renders inside it. `display: contents` keeps it out of the layout entirely, so it cannot
 * affect the flex rows it sits inside.
 */
const Hook = styled.div`
	display: contents;
`;

type WorkDetailPanelProps = {
	rows: WorkDetailRow[];
	showRates: boolean;
	quantityDecimals: number;
	onChange: (rows: WorkDetailRow[]) => void;
	/** The dimension the rows were grouped by, which names the bucket that dimension cannot reach. */
	grouping: string;
	startDate?: string;
	endDate?: string;
	/**
	 * Whether a selected project is one this viewer lacks full access to. `Project.timeRegistrations`
	 * returns an empty connection in that case, so "no rows" then says nothing about whether time was
	 * logged and the empty state must not claim it does.
	 */
	hasRestrictedProject?: boolean;
};

/**
 * The work completed behind a fixed-price invoice, shown as supporting detail while the invoice is
 * being created.
 *
 * Renders NOTHING priced unless the company has opted in to rate disclosure — no unit price, no
 * amount, no tax, no discount — and it is not an invoice row: nothing here changes what is charged.
 * The only edit it offers is taking a row off the invoice, which is why the action says "Remove
 * from invoice" rather than "Delete": these rows name real people and real logged hours, and
 * "Delete" would read as destroying the time registration itself. There is deliberately no
 * duplicate action, since duplicating a row would assert the work happened twice.
 *
 * Removals are reported back as `removed: true` rather than by dropping the row, so they persist
 * with the invoice and can be restored.
 */
/** One row as the table renders it: hours resolved, dates collapsed to a single display string. */
export type WorkDetailDisplayRow = {
	id: string;
	groupLabel: string;
	hours: number;
	dates: string;
	rate: number | null;
	value: number | null;
};

/** Collapses a row's first/last date to one display string; a single-day row shows one date. */
export const workDetailDateRange = (row: WorkDetailRow): string => {
	if (!row.firstDate) return '';
	return row.lastDate && row.lastDate !== row.firstDate ? `${row.firstDate} – ${row.lastDate}` : row.firstDate;
};

/**
 * The rows the table is given: the operator's removals dropped, hours resolved for display.
 *
 * Exported and tested directly rather than through the rendered table, because the design-system
 * Table virtualizes its rows and renders an empty body in jsdom without a measurable scroll
 * container — so asserting row content through it would pin the test to that environment quirk
 * rather than to this component's behaviour.
 */
export const workDetailPanelTableRows = (
	rows: WorkDetailRow[],
	quantityDecimals: number,
	resolveGroupLabel: (groupLabel: string | null) => string = groupLabel => groupLabel ?? ''
): WorkDetailDisplayRow[] =>
	rows
		.filter(row => !row.removed)
		.map(row => ({
			id: row.key,
			groupLabel: resolveGroupLabel(row.groupLabel),
			hours: workDetailHours(row.minutes, quantityDecimals),
			dates: workDetailDateRange(row),
			rate: row.rate,
			value: row.value,
		}));

/** Marks one row removed or restored, preserving list order and every other row. */
export const toggleWorkDetailRowRemoved = (rows: WorkDetailRow[], key: string, removed: boolean): WorkDetailRow[] =>
	rows.map(row => (row.key === key ? {...row, removed} : row));

export const WorkDetailPanel = ({
	rows,
	showRates,
	quantityDecimals,
	onChange,
	grouping,
	startDate,
	endDate,
	hasRestrictedProject = false,
}: WorkDetailPanelProps) => {
	const {formatMessage} = useIntl();

	const setRemoved = (key: string, removed: boolean) => onChange(toggleWorkDetailRowRemoved(rows, key, removed));

	// A null label means the grouping dimension was absent for that row's time, so the wording follows
	// the dimension. An empty label stays empty — that is a billed-deliverable placeholder row.
	const resolveGroupLabel = (groupLabel: string | null): string =>
		groupLabel == null ? formatMessage({id: workDetailUngroupedLabelKey(grouping)}) : groupLabel;

	const removed = rows.filter(row => row.removed);
	const totalHours = workDetailHours(workDetailTotalMinutes(rows), quantityDecimals);
	const totalValue = workDetailTotalValue(rows);

	if (rows.length === 0) {
		// An inaccessible project yields an empty time-registration connection, so the absence of rows
		// is explained by access and says nothing about whether time was logged. Claiming "no time
		// logged" there would state as fact something this component cannot see.
		return (
			<Hook data-testid="work-detail-empty" data-cy="work-detail-empty">
				<Text>
					{hasRestrictedProject
						? formatMessage({id: 'invoicing.work_detail.empty_restricted_access'})
						: formatMessage(
								{id: 'invoicing.work_detail.empty'},
								{startDate: startDate ?? '', endDate: endDate ?? ''}
						  )}
				</Text>
			</Hook>
		);
	}

	const tableData = workDetailPanelTableRows(rows, quantityDecimals, resolveGroupLabel);

	return (
		<Hook data-testid="work-detail-panel" data-cy="work-detail-panel">
			<Heading size="4">{formatMessage({id: 'invoicing.work_detail.section_title'})}</Heading>
			<Hook data-testid="work-detail-caption">
				<Text size="2" color="auxiliary">
					{formatMessage({id: 'invoicing.work_detail.not_charged_caption'})}
				</Text>
			</Hook>

			<Table data={tableData} data-cy="work-detail-table">
				<Table.TextColumn header={formatMessage({id: 'common.description'})} accessorKey="groupLabel" />
				<Table.NumberColumn header={formatMessage({id: 'invoicing.work_detail.column_hours'})} accessorKey="hours" />
				<Table.TextColumn header={formatMessage({id: 'invoicing.work_detail.column_dates'})} accessorKey="dates" />
				{showRates ? (
					<Table.NumberColumn header={formatMessage({id: 'invoicing.work_detail.column_rate'})} accessorKey="rate" />
				) : undefined}
				{showRates ? (
					<Table.NumberColumn
						header={formatMessage({id: 'invoicing.work_detail.column_value'})}
						accessorKey="value"
					/>
				) : undefined}
				<Table.ActionColumn header={formatMessage({id: 'common.actions'})}>
					<Table.ActionColumn.Option
						data-cy="work-detail-row-remove"
						onClick={(row: {id: string}) => setRemoved(row.id, true)}
					>
						{formatMessage({id: 'invoicing.work_detail.remove_row'})}
					</Table.ActionColumn.Option>
				</Table.ActionColumn>
			</Table>

			<Hook data-testid="work-detail-totals">
				<FlexRow justifyContent="flex-end" gap="s">
					<Text>{formatMessage({id: 'invoicing.work_detail.total_hours'})}</Text>
					<Hook data-testid="work-detail-total-hours" data-cy="work-detail-total-hours">
						<Text>{totalHours}</Text>
					</Hook>
					{showRates && totalValue !== null ? (
						<>
							<Text>{formatMessage({id: 'invoicing.work_detail.total_value'})}</Text>
							<Hook data-testid="work-detail-total-value" data-cy="work-detail-total-value">
								<Text>{totalValue}</Text>
							</Hook>
						</>
					) : null}
				</FlexRow>
			</Hook>

			{removed.length > 0 ? (
				<Hook data-testid="work-detail-removed">
					<FlexColumn gap="s">
						<Hook data-testid="work-detail-removed-count">
							<Text size="2" color="auxiliary">
								{formatMessage({id: 'invoicing.work_detail.removed_count'}, {count: removed.length})}
							</Text>
						</Hook>
						{removed.map((row, index) => (
							<FlexRow key={row.key} alignItems="center" gap="s">
								<Text>{resolveGroupLabel(row.groupLabel)}</Text>
								{/* Button spreads unknown props onto its underlying button element, so unlike
								    the text and layout components it carries a data-testid itself. */}
								<Button
									emphasis={EMPHASIS.LOW}
									onPress={() => setRemoved(row.key, false)}
									data-testid={`work-detail-row-restore-${index}`}
									data-cy={`work-detail-row-restore-${index}`}
								>
									{formatMessage({id: 'invoicing.work_detail.restore_row'})}
								</Button>
							</FlexRow>
						))}
					</FlexColumn>
				</Hook>
			) : null}
		</Hook>
	);
};

export default WorkDetailPanel;
